import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Button } from "@/components/flexkit/Button";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import type {
    WorkflowEditFormStepData,
    WorkflowEditFormStepType,
} from "@/components/automations/automation-edit-form-data";
import { useId, useMemo, type FC } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/components/podkit/lib/cn";
import { StepEllipse } from "@/components/automations/StepEllipse";
import IconDotGrid2x3 from "central-icons/IconDotGrid2x3";
import { StepVerticalLine } from "@/components/automations/StepVerticalLine";
import { forwardRef } from "react";

type WorkflowStepItemProps = {
    step: WorkflowEditFormStepData;
    onUpdate: (updates: WorkflowEditFormStepData) => void;
    onRemove: () => void;
};

// Following the recommended pattern from https://docs.dndkit.com/presets/sortable#drag-overlay
// this component is split into a presentational component and a sortable wrapper component
export const SortableWorkflowEditStepItem: FC<WorkflowStepItemProps> = (props) => {
    const sortable = useSortable({
        id: props.step.id,
    });

    const { attributes, listeners, setNodeRef, transform, transition, isDragging, newIndex } = sortable;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <WorkflowEditStepItem
            ref={setNodeRef}
            isDragOverlay={false}
            style={style}
            {...attributes}
            {...listeners}
            {...props}
            isDragging={isDragging}
            newIndex={newIndex}
        />
    );
};

export const WorkflowEditStepItem = forwardRef<
    HTMLDivElement,
    WorkflowStepItemProps & {
        style?: React.HTMLAttributes<HTMLDivElement>["style"];
        isDragOverlay: boolean;
        isDragging?: boolean;
        newIndex?: number;
    }
>(({ isDragOverlay, step, onUpdate, onRemove, isDragging, newIndex, style, ...props }, ref) => {
    const contentId = useId();

    const handleTypeChange = (type: WorkflowEditFormStepType) => {
        onUpdate({ ...step, type });
    };

    const handleContentChange = (content: string) => {
        onUpdate({ ...step, content });
    };

    const { placeholder } = useMemo<{ placeholder: string }>(() => {
        switch (step.type) {
            case "command":
                return { placeholder: "Command to run" };
            case "prompt":
                return { placeholder: "Prompt text" };
            default:
                return { placeholder: "Content" };
        }
    }, [step.type]);

    const hideTopLine = newIndex === 0;

    return (
        <div
            ref={ref}
            style={style}
            className={cn("rounded-lg border-l border-transparent bg-surface-primary", {
                "ml-6 border border-border-base": isDragOverlay,
                "rounded-xl border border-border-brand bg-surface-brand shadow": isDragging,
            })}
            {...props}
        >
            <div className="flex">
                <div className="flex w-6 flex-col items-center self-stretch">
                    <StepVerticalLine
                        variant={isDragging ? "brand" : "neutral"}
                        className={cn("h-[10px] grow-0", {
                            "border-transparent": hideTopLine,
                        })}
                    />
                    <StepEllipse className={cn("shrink-0 text-border-strong", { "text-content-brand": isDragging })} />
                    <StepVerticalLine variant={isDragging ? "brand" : "neutral"} />
                    {!isDragging && (
                        <button
                            type="button"
                            className={cn(
                                "cursor-grab items-start text-content-tertiary hover:text-content-secondary active:cursor-grabbing",
                            )}
                            aria-label="Drag to reorder"
                        >
                            <IconDotGrid2x3 size={16} />
                        </button>
                    )}
                    <StepVerticalLine variant={isDragging ? "brand" : "neutral"} />
                </div>

                <div className={cn("flex flex-1 flex-col gap-1 px-2 pb-2", { "opacity-0": isDragging })}>
                    <div className="flex items-center justify-between">
                        <Select value={step.type} onValueChange={handleTypeChange}>
                            <SelectTrigger loading={false} className="w-fit border-0 px-2 py-1.5">
                                <SelectValue placeholder="Select step type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem key="command" value="command">
                                    <span>Command</span>
                                </SelectItem>
                                <SelectItem key="prompt" value="prompt">
                                    <span>Prompt</span>
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={onRemove}
                            data-testid="remove-step-button"
                            LeadingIcon={IconTrash}
                            data-tracking-id="remove-step-workflow-edit-step-item"
                        />
                    </div>

                    <Textarea
                        id={contentId}
                        value={step.content}
                        className="w-full max-w-full resize-none"
                        onChange={(e) => handleContentChange(e.target.value)}
                        // Otherwise enter will trigger keyboard based step reordering
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder={placeholder}
                        maxHeight={400}
                        required
                    />
                </div>
            </div>
        </div>
    );
});

WorkflowEditStepItem.displayName = "WorkflowEditStepItem";
